/**
 * Minimal SPA router — no react-router dependency.
 *
 * Listens to `popstate` (back/forward) and a custom `vc:navigate` event
 * (fired by `navigate()`) to keep React state in sync with the URL bar.
 * CloudFront serves `index.html` on 404 so deep-link refresh works.
 */
import { useEffect, useState } from 'react';

const NAV_EVENT = 'vc:navigate';

export function useLocation(): { pathname: string; search: string; hash: string } {
  const [loc, setLoc] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  }));

  useEffect(() => {
    const onChange = () =>
      setLoc({
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      });
    window.addEventListener('popstate', onChange);
    window.addEventListener(NAV_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(NAV_EVENT, onChange);
    };
  }, []);

  return loc;
}

export function navigate(path: string): void {
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  if (current === path) {
    // Re-clicking a hash link (e.g. "Get started" → /#prompt while
    // already there): re-scroll instead of silently doing nothing.
    const hashIdx = path.indexOf('#');
    if (hashIdx !== -1) {
      const el = document.getElementById(path.slice(hashIdx + 1));
      el?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
    }
    return;
  }
  window.history.pushState({}, '', path);
  window.dispatchEvent(new Event(NAV_EVENT));

  // Scroll handling lives in App via useScrollToHash() so it survives
  // React commits and works in background tabs (rAF is throttled when
  // the tab isn't visible). Without a hash, snap to top here so plain
  // page-to-page navigation behaves as before.
  if (!path.includes('#')) {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }
}

/**
 * Mount in the top-level component. Scrolls to the element matching
 * the current URL hash after each render, with a small retry window
 * for the case where the target hasn't mounted yet (e.g., navigating
 * from `/push` to `/#pricing` — the pricing section only exists after
 * Home renders).
 */
export function useScrollToHash(hash: string, pathname: string): void {
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        return;
      }
      // Retry while the target page is mounting; cap at ~500ms.
      if (++attempts < 10) setTimeout(tryScroll, 50);
    };
    tryScroll();
  }, [hash, pathname]);
}

/**
 * `<Link to="/claude">…</Link>` — internal link that goes through the SPA
 * router. Falls back to a normal anchor if the user cmd/middle-clicks.
 */
export function Link(
  props: React.AnchorHTMLAttributes<HTMLAnchorElement> & { to: string },
) {
  const { to, onClick, children, ...rest } = props;
  return (
    <a
      href={to}
      onClick={(e) => {
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey
        ) {
          return;
        }
        e.preventDefault();
        onClick?.(e);
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
